<template>
  <div class="controls">
    <div class="controls__start">
      <button
        id="validate-session-details-back-btn"
        v-tooltip="'Navigate to parent training session'"
        class="button"
        aria-label="Back"
        @click="router.go(-1)"
      >
        <SVGIcon
          class="me-1"
          :iconName="'chevron_left'"
          :width="'18px'"
          :height="'18px'"
        />
        <span>Back</span>
      </button>
      <HelpTip>
        Select a tab below to see various validate session details.
      </HelpTip>
    </div>
    <div class="controls__end">
      <div class="controls__actions">
        <button v-tooltip="'Retrieve info, logs, metrics for validate session.'" class="button" @click="handleRefreshPage">
          <SVGIcon
            :iconName="'refresh'"
            :width="'18px'"
            :height="'18px'"
          />Refresh
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="'Terminate validation but keep all logs and metrics.'"
          class="button"
          @click="openConfirmStopValidateSessionModal"
        >
          <SVGIcon
            :iconName="'stop_circle'"
            :width="'18px'"
            :height="'18px'"
          />
          Stop
        </button>
        <button
          v-if="permissions && permissions.trainer_write"
          v-tooltip="'Move validate session to recycle bin. Session metrics will remain until purged.'"
          class="button delete"
          @click="openConfirmDeleteSessionModal"
        >
          <SVGIcon
            :iconName="'trash'"
            :width="'18px'"
            :height="'18px'"
          />
          Recycle Bin
        </button>
      </div>
    </div>
  </div>
  <div class="validate-session scrollbar">
    <div class="session-data">
      <div class="tab-selector mb-1">
        <input
          id="option-info"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'info'"
        >
        <label for="option-info">Session</label>
        <input
          id="option-logs"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'logs'"
        >
        <label for="option-logs">Logs</label>
        <input
          id="option-charts"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'charts'"
        >
        <label for="option-charts">Charts</label>
        <input
          id="option-metrics"
          v-model="tab"
          type="radio"
          name="tab-selector"
          :value="'metrics'"
        >
        <label for="option-metrics">Metrics</label>
      </div>
      <div v-if="tab==='info'" class="params session-info-layout">
        <ValidatorSessionData
          :currentValidateSession="currentValidateSession"
          :currentTrainingSession="currentTrainingSession"
          :currentValidateSessionStatus="currentValidateSessionStatus"
          :dataset="dataset"
          :user="user"
          :loading="loading"
          @navigate-to-training-session="navigateToTrainingSession"
          @to-gallery="handleToGallery"
        />
        <SessionParameters :modelParameters="model_parameters" />
      </div>
      <div v-else-if="tab==='metrics'" class="params">
        <ValidatorMetrics :metrics="metrics" />
      </div>
      <div v-else-if="tab==='logs'" class="system-logs">
        <div id="validate-session-system-logs" class="text-area-box scrollbar">
          <RecycleScroller
            v-slot="{ item }"
            class="logs-scroller"
            :items="logLines"
            :item-size="20"
          >
            <div class="log-line">{{ item }}</div>
          </RecycleScroller>
        </div>
      </div>
      <div v-else-if="tab==='charts'" class="params">
        <ValidatorCharts
          ref="validatorCharts"
          :sessionIds="[currentValidateSession.id]"
        />
      </div>
    </div>
  </div>
  <ConfirmModal
    ref="confirmModal"
    :messageHeader="confirmMessageHeader"
    :message="confirmMessage"
    :buttonClass="'button-delete'"
    :buttonText="buttonText"
    @confirmed="confirmFunction"
  />
</template>

<script setup>
import {
  ref, computed, onBeforeUnmount, onMounted, watch, inject,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import * as YAML from 'yaml';
import { useTimeoutPoll } from '@vueuse/core';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import { RecycleScroller } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';
import { useStore } from 'vuex';
import ValidatorCharts from '@/components/TrainerComponent/TrainerExperimentAndSessions/ValidatorCharts.vue';
import ValidatorSessionData from '@/components/TrainerComponent/TrainerExperimentAndSessions/ValidatorSessionData.vue';
import ValidatorMetrics from '@/components/TrainerComponent/TrainerExperimentAndSessions/ValidatorMetrics.vue';
import SessionParameters from '@/components/TrainerComponent/TrainerExperimentAndSessions/SessionParameters.vue';
import SVGIcon from '@/components/SVGIcon.vue';
import ConfirmModal from '@/components/ConfirmModal.vue';
import HelpTip from '../../HelpTip.vue';

// Refs
const isPublic = inject('isPublic', false);
const store = useStore();
const tab = ref('info');
const logs = ref(null);
const logLines = ref([]);
const $toast = useToast();
const route = useRoute();
const router = useRouter();
const currentValidateSessionStatus = ref('');
const confirmMessage = ref(null);
const confirmMessageHeader = ref(null);
const toDelete = ref(null);
const confirmFunction = ref(null);
const buttonText = ref('Delete');
const confirmModal = ref(null);
const dataset = ref(null);
const hideProgress = ref(false);
const loading = ref(false);
const validatorCharts = ref(null);
const permissions = ref(null);

const userList = computed(() => store.state.user.userList);
const user = computed(() => userList.value?.find((e) => e.username === currentValidateSession.value?.docker_task.username));

const currentProject = computed(() => store.state.projects.currentProject);

const currentExperiment = computed(() => store.state.experiments.currentExperiment);
const routeSessionId = ref(null);

const currentValidateSession = computed({
  // getter
  get() {
    return store.state.experiments.currentValidateSession;
  },
  // setter
  set(session) {
    // Note: we are using destructuring assignment syntax here.
    store.commit('experiments/setCurrentValidateSession', session);
  },
});

const currentTrainingSession = computed({
  get() {
    return store.state.experiments.currentSession;
  },
  set(session) {
    // Note: we are using destructuring assignment syntax here.
    store.commit('experiments/setCurrentSession', session);
  },
});

const model_parameters = computed(() => YAML.stringify(currentValidateSession.value?.params?.model_params || {}, { indent: 8 }));

const metrics = computed(() => YAML.stringify(currentValidateSession.value?.results || {}, { indent: 8 }));

// Lifecycle hooks

const getLogs = async () => {
  if (currentValidateSession.value && currentValidateSession.value.id) {
    const dataConnect = new DatastoreConnect();
    const resp = await dataConnect.getValidateSessionLogs({
      validate_session_id: currentValidateSession.value.id,
    });

    let temp = "";
    if (!resp || resp.error || !resp.result) {
      temp += `No logs file to display`;
    } else {
      temp += resp.result;
    }

    logs.value = temp;
  } else {
    logs.value = null;
  }

  if (logs.value) {
    logLines.value = logs.value.split('\n');
  }
};

const { isActive: isGetLogsActive, pause: pauseGetLogs, resume: resumeGetLogs } = useTimeoutPoll(getLogs, 5000);

const handleRefreshPage = () => {
  if (tab.value === "charts") {
    validatorCharts.value.fetchCharts();
  } else if (tab.value === "logs") {
    getLogs();
  }
  handleGetSession();
};

const handleGetSession = async () => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.getValidateSession({ validate_session_id: routeSessionId.value })
    .then(async (data) => {
      if (data.result) {
        const date1 = new Date(data.result.deleted);
        const date2 = new Date("0001-01-01T00:00:00Z");
        if (date1 > date2) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: Session has been removed`,
            type: "error",
            position: "top",
          });
          router.push({ name: `${isPublic ? 'public.' : ''}experiment.training.list`, query: { exp_id: data.result.trainer_id } });
        } else {
          currentValidateSession.value = data.result;
          currentValidateSessionStatus.value = data.result.docker_task?.status;
        }
      } else if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve experiment: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve experiment: ${error}`,
        type: "error",
        position: "top",
      });
    });
};

const { isActive: isGetSessionsActive, pause: pauseGetSession, resume: resumeGetSession } = useTimeoutPoll(handleGetSession, 30000);

const handleToGallery = () => {
  if (dataset.value) {
    router.push({ path: '/'+currentProject.value.id+'/datasets/gallery/main', query: { dataset: dataset.value.id } });
  }
};

const openConfirmStopValidateSessionModal = () => {
  if (currentValidateSession.value) {
    confirmMessage.value = `Are you sure you want to stop validate session "${currentValidateSession.value.name}"?\n You will need to restart validate to resume.`;
    confirmMessageHeader.value = 'Stop Validate Session';
    buttonText.value = 'Stop';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      handleStopValidateSessions([currentValidateSession.value.id]);
    };
  }
};
const openConfirmDeleteSessionModal = () => {
  if (currentValidateSession.value) {
    confirmMessage.value = `Are you sure you want to move validate session "${currentValidateSession.value.docker_task.name}" to recycle bin? Session metrics will remain until purged`;
    confirmMessageHeader.value = 'Remove Validate Session';
    buttonText.value = 'Remove';
    confirmModal.value.showModal();
    confirmFunction.value = () => {
      router.push({ name: `${isPublic ? 'public.' : ''}experiment.training.list`, query: { exp_id: currentValidateSession.value.trainer_id } });
      handleDeleteSession();
    };
  }
};

const handleStopValidateSessions = async (ids) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.terminateValidateSessions({
    session_ids: ids,
  })
    .then((data) => {
      handleGetSession();
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to stop validate session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to stop validate session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};
const handleDeleteSession = async (id) => {
  const dataConnect = new DatastoreConnect();
  await dataConnect.deleteValidateSessions({
    session_ids: [id],
  })
    .then(async (data) => {
      if (data.error) {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to delete validate session: ${data.error.message}`,
          type: "error",
          position: "top",
        });
      }
      navigateToValidateSessions();
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to delete validate session: ${error}`,
        type: "error",
        position: "top",
      });
    });

  toDelete.value = null;
};

const getDataset = async (dataset_id) => {
  const dataConnect = new DatastoreConnect();
  if (dataset_id) {
    await dataConnect.getDatasetById({
      dataset_id,
    })
      .then((resp) => {
        if (resp.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to get dataset: ${resp.error.message}`,
            type: "error",
            position: "top",
          });
        } else if (resp.result) {
          dataset.value = resp.result;
        }
      })
      .catch((error) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to get dataset: ${error}`,
          type: "error",
          position: "top",
        });
      });
  }
};

const getTrainingSession = async (id) => {
  const dataConnect = new DatastoreConnect();
  const resp = await dataConnect.getTrainingSession({
    trainer_session_id: id,
  })
    .catch((e) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to retrieve training session: ${e}`,
        type: "error",
        position: "top",
      });
    });
  if (!resp || resp.error || !resp.result) {
    $toast.open({
      queue: true,
      dismissible: true,
      message: `Failed to retrieve training session: ${resp.error.message}`,
      type: "error",
      position: "top",
    });
    return null;
  }
  return resp.result;
};

async function getProjectAccessForSingleUser() {
  if (currentProject.value && store.state.user?.user?.username) {
    const dataConnect = new DatastoreConnect();
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentProject.value.id,
      username: store.state.user.user.username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        } else if (data.error) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve project access: ${data.error.message}`,
            type: "error",
            position: "top",
          });
        }
      })
      .catch((e) => {
        $toast.open({
          queue: true,
          dismissible: true,
          message: `Failed to retrieve project access: ${e}`,
          type: "error",
          position: "top",
        });
      });
  }
}

const handleProgressToggle = (e) => {
  hideProgress.value = e;
};

const navigateToValidateSessions = () => {
  router.push({ name: `${isPublic ? 'public.' : ''}experiment.validation.list`, query: { exp_id: currentExperiment.value.id } });
};

const navigateToTrainingSession = (id) => {
  router.push({ name: `${isPublic ? 'public.' : ''}experiment.training.details`, query: { train_session_id: id } });
};

watch(tab, () => {
  router.replace({ name: `${isPublic ? 'public.' : ''}experiment.validation.details`, query: { val_session_id: currentValidateSession.value.id, mode: tab.value } });

  if (tab.value === 'logs') {
    resumeGetLogs();
  } else {
    pauseGetLogs();
  }

  if (tab.value === 'info') {
    resumeGetSession();
  } else {
    pauseGetSession();
  }
});

onBeforeUnmount(() => {
  pauseGetLogs();
  pauseGetSession();
});

onMounted(async () => {
  loading.value = true;
  const mode = route.query.mode;
  if (mode) {
    tab.value = mode;
  }
  
  // Handle both numeric ID and hex UID format (v-hexvalue)
  const sessionIdParam = route.query.val_session_id;
  let sessionId = null;
  
  if (sessionIdParam) {
    if (sessionIdParam.startsWith('v-')) {
      // Convert hex format (v-7bf) to decimal
      sessionId = parseInt(sessionIdParam.substring(2), 16);
    } else {
      // Try to parse as numeric, otherwise use as-is
      const parsedId = parseInt(sessionIdParam);
      sessionId = isNaN(parsedId) ? sessionIdParam : parsedId;
    }
  }
  routeSessionId.value = sessionId;

  // Get the validation session first
  await handleGetSession();

  // Then use the training_session_id from the validation session object
  const trainSessionId = currentValidateSession.value?.training_session_id;
  if (trainSessionId) {
    if (!currentTrainingSession.value || currentTrainingSession.value.id !== trainSessionId) {
      currentTrainingSession.value = await getTrainingSession(trainSessionId);
    }
  }

  if (!routeSessionId.value && currentValidateSession.value) {
    routeSessionId.value = currentValidateSession.value.id;
    router.replace({ name: `${isPublic ? 'public.' : ''}experiment.validation.details`, query: { val_session_id: routeSessionId.value, mode } });
  }

  getProjectAccessForSingleUser();

  await handleGetSession();

  await getDataset(currentValidateSession.value.dataset_id);

  if (tab.value === 'info') {
    resumeGetSession();
  }

  loading.value = false;
});

</script>

<style lang="scss" scoped>
.controls {
  display: flex;
  margin: 20px 20px 0 20px;
  align-items: center;
  gap: 10px;

  &__count {
    font-weight: bold;
    font-size: 1rem;
  }

  &__start {
    display: flex;
    justify-content: flex-start;
    width: fit-content;
    align-items: center;
    gap: 10px;
  }

  &__end {
    display: flex;
    justify-content: flex-end;
    flex: 1;
  }

  &__actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    height: 100%;
  }
}

@media (max-width: $breakpoint-sm) {
  .controls {
    flex-direction: column;
    &__start{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__middle{
      display: flex;
      align-items: flex-start;
      flex-direction: column;
      width: 100%;
    }

    &__end{
      display: flex;
      align-items: flex-end;
      flex-direction: column;
      width: 100%;
    }
  }
}

.validate-session{
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  overflow-x: auto;
}

.session-data {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  height: 100%;
  width: calc(100% - 400px);
  padding: 16px;

  ul {
    list-style-type: none;
  }

  h3 {
    margin: 0;
    margin-bottom: 10px;
    font-size: 1rem;
    text-align: left;
    @include themify() {
      color: themed('body-text-color-secondary');
    }
  }

  .text-area-box {
    display: flex;
    flex-direction: row;
    border-radius: 4px;
    background: #121212;
    border: 1px solid grey;
    height: 100%;
    // padding: 15px;
    overflow-y: auto;

    span {
      color: white;
      font-weight: 700;
      font-size: 0.9rem;

      pre {
        font-family: 'Courier New', monospace !important;
        text-align: left;
      }
    }
  }

  .text-area-details-white {
    display: grid;
    grid-template-columns: repeat(auto-fit, 450px);
    grid-auto-rows: 50px;
    grid-gap: 20px 5px;
    height: 100%;
    border-radius: 4px;
    background: var(--color-white-100);
    border: 1px solid var(--color-primary-100);
    padding: 15px;
    overflow-y: auto;

    .list-item {
      height: fit-content;
      display: flex;
      flex-direction: column;
      text-align: left;
      padding: 5px;

      label {
        @include themify() {
          color: tint-color(themed('body-text-color-secondary'), 20%);
        }
        font-weight: 700;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }

      span {
        @include themify() {
          color: themed('body-text-color');
        }
        font-weight: 600;
        font-size: 0.9rem;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
  }

  .system-logs, .metrics, .params {
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }
}

.model-params {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 4px;
  background: var(--color-white-100);
  border: 1px solid grey;
  overflow-y: auto;
}

.tab-selector {
  display: flex;
  flex-direction: row;
  border-bottom: 1px solid #ddd;
}

.tab-selector input[type="radio"] {
  display: none;
}

.tab-selector label {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  height: 32px;
  width: fit-content;
  font-size: 0.9rem;
  font-weight: bold;
  color: var(--body-text-color-heading);
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}

.tab-selector input[type="radio"]:checked+label {
  @include themify() {
    color: themed('color-primary-500');
    border-bottom: 2px solid themed('color-primary-500');
  }
}

.session-info-layout {
  display: grid !important;
  grid-template-columns: repeat(2, 1fr);
  gap: 0;
  align-items: start;
  flex-direction: unset !important;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
}

.logs-scroller {
  height: 100%;
  width: 100%;
}

.log-line {
  text-align: left;
  white-space: pre;
  color: white;
}

:deep(.vue-recycle-scroller__item-wrapper) {
  overflow: visible !important;
}

:deep(.vue-recycle-scroller) {
  overflow: auto !important;
  padding: 15px;
}

.button {
    background: white;
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 26px;
    padding: 0;
    box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.2), 0 0 1px 1px rgba(0, 0, 0, 1);
    padding: 4px 8px;
    gap: 2px;
    width: fit-content;

    @include themify() {
      color: themed('body-text-color')
    }

    &.highlight {
      background: var(--button-color);
      color: var(--button-text-color);

      svg {
        margin-right: 5px;
      }
    }

    &:hover {
      background: rgba(var(--color-primary-100-rgb), 0.5);
    }

    &.delete:not([disabled]) {
      @include themify() {
        background: themed('button-delete');
        color: themed('button-delete-text');
      }
    }

    &.delete[disabled] {
      border: none;
    }

    &.delete:hover:not([disabled]) {
      @include themify() {
        background: $color-red-300;
        color: themed('button-delete-text');
      }
    }
  }
</style>
